import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';
import { useMiniappSdkEventHandler } from 'app/shared/hooks/use-miniapp-sdk';
import { ActivitySegment } from 'app/data/types/activity';
import { ActivityFilter, resolveActivityFilter } from 'app/shared/consts/activity';

/** Where the last-used view is kept. Namespaced — host storage is shared across every miniapp. */
export const FEED_VIEW_STORAGE_KEY = 'activity-center.feed-view';

/**
 * How long a remembered view stays worth restoring.
 *
 * Short on purpose. Coming back from a booking a minute later, the pane you left is where you want to
 * be; opening the app fresh tomorrow, it is a filter you have forgotten setting and an empty list you
 * cannot explain. Five minutes reads as "still the same trip".
 */
export const FEED_VIEW_TTL_MS = 5 * 60 * 1000;

/**
 * How long to wait for the host to answer a storage read before giving up on it.
 *
 * `storage.get` is routed per action name, so a host that never registered a handler drops it in
 * silence — no reply, no error. Nothing here blocks on the answer, so this only bounds how long a late
 * reply may still be applied; past it the read is abandoned rather than landing under the user.
 */
export const RESTORE_TIMEOUT_MS = 2_000;

const SEGMENTS: ActivitySegment[] = ['upcoming', 'completed'];

interface StoredFeedView {
  segment: ActivitySegment;
  filter: ActivityFilter;
  savedAt: number;
}

/**
 * The view as the string handed to the host: JSON, percent-encoded.
 *
 * Encoded because the native host does not escape what it stores. It answers a read by pasting the
 * value raw into `{"key":"…","value":"<value>"}`, so a plain JSON value closes that string at its
 * first quote. The sdk then calls `JSON.parse` on the result with no guard, and the throw lands in
 * its event listener (a Sentry `SyntaxError ... at position 46`, which is exactly the `s` of
 * `segment`). The restore never hears back and falls to its timeout.
 *
 * `encodeURIComponent` leaves no `"` and no `\`, so the host's pasted string stays valid JSON. It is
 * also a string, which the sdk passes through rather than stringifying again.
 */
export const encodeStoredView = (view: StoredFeedView): string => encodeURIComponent(JSON.stringify(view));

/**
 * Plain JSON is still accepted. The tenant portal writes this key itself, as `JSON.stringify` into
 * localStorage (`remembered-feed-view.ts`), and the web host hands that back escaped properly. So do
 * entries this app wrote before encoding. Anything else is read as encoded.
 */
const decodeStoredView = (raw: string): unknown => JSON.parse(raw.startsWith('{') ? raw : decodeURIComponent(raw));

const parseStoredView = (raw: unknown): StoredFeedView | null => {
  if (typeof raw !== 'string' || !raw) {
    return null;
  }

  try {
    const parsed = decodeStoredView(raw) as Partial<StoredFeedView>;
    const { segment } = parsed;
    // Validate rather than trust: this came from storage the user's host owns, and a stale entry
    // written by an older build could name a segment or chip this one no longer has.
    if (!segment || !SEGMENTS.includes(segment) || typeof parsed.savedAt !== 'number') {
      return null;
    }

    return { segment, filter: resolveActivityFilter(parsed.filter ?? null), savedAt: parsed.savedAt };
  } catch {
    return null;
  }
};

/**
 * Remember the pane and chip the user is on, so returning to the feed returns them to it.
 *
 * The URL already carries both, which covers going back within the webview. It cannot cover the host
 * relaunching the miniapp at its configured launch URL, which mounts a fresh app with no query string
 * to read — and that is the common case coming back from another miniapp.
 */
export const useRememberFeedView = (): ((segment: ActivitySegment, filter: ActivityFilter) => void) => {
  const client = useMiniappSdk();

  return useCallback(
    (segment: ActivitySegment, filter: ActivityFilter) => {
      client?.storage.set(FEED_VIEW_STORAGE_KEY, encodeStoredView({ segment, filter, savedAt: Date.now() }));
    },
    [client],
  );
};

/**
 * Restore that view on a cold start, by writing it into the URL.
 *
 * Writes to the URL rather than holding its own state so the URL stays the single source of truth —
 * everything downstream already reads the view from there, and a restored view remains shareable.
 *
 * A URL that already names a view always wins: it came from a deeplink or a back navigation, both of
 * which are a deliberate destination, where storage is only a guess about intent.
 */
/** The view the URL names, as one comparable value. Absent params are part of the identity. */
const viewSignature = (params: URLSearchParams): string => `${params.get('tab') ?? ''}|${params.get('filter') ?? ''}`;

export const useRestoreFeedView = (): void => {
  const [searchParams, setSearchParams] = useSearchParams();
  const client = useMiniappSdk();
  const isSettled = useRef(false);
  const hasRequested = useRef(false);

  // How many times the URL's view has actually changed, not what it currently is. A signature alone
  // cannot tell "never moved" from "moved and moved back", and the second is the case that matters:
  // the end state looks untouched while the user has plainly expressed an intent.
  const viewRevision = useRef(0);
  const lastView = useRef(viewSignature(searchParams));
  // Counted in a layout effect rather than during render: a render React discards would otherwise
  // bump the count for a view that was never committed. Layout effects run before the read effect.
  useLayoutEffect(() => {
    const next = viewSignature(searchParams);
    if (next !== lastView.current) {
      lastView.current = next;
      viewRevision.current += 1;
    }
  }, [searchParams]);

  const revisionAtRead = useRef(0);

  const hasViewInUrl = searchParams.has('tab') || searchParams.has('filter');

  useEffect(() => {
    if (isSettled.current || hasRequested.current || hasViewInUrl || !client) {
      return undefined;
    }

    // One read per mount. Without this the effect re-fires the moment the URL goes back to naming no
    // view, re-capturing the revision and losing the very evidence that the user moved.
    hasRequested.current = true;
    revisionAtRead.current = viewRevision.current;
    client.storage.get(FEED_VIEW_STORAGE_KEY);
    const timer = setTimeout(() => {
      isSettled.current = true;
    }, RESTORE_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [client, hasViewInUrl]);

  useMiniappSdkEventHandler(
    'getStorageResponse',
    (data: unknown) => {
      // The host answers `{ [key]: value }`, keyed by what was asked for — so a reply for somebody
      // else's key is not ours to read.
      if (isSettled.current || !data || typeof data !== 'object' || !(FEED_VIEW_STORAGE_KEY in data)) {
        return;
      }

      isSettled.current = true;
      // The user moved while we were waiting. Restoring now would undo a deliberate choice — whichever
      // direction it went, and even if it ended up back where it started.
      if (viewRevision.current !== revisionAtRead.current) {
        return;
      }

      const stored = parseStoredView((data as Record<string, unknown>)[FEED_VIEW_STORAGE_KEY]);
      if (!stored || Date.now() - stored.savedAt > FEED_VIEW_TTL_MS) {
        return;
      }

      const params = new URLSearchParams(searchParams);
      params.set('tab', stored.segment);
      params.set('filter', stored.filter);
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams],
  );
};
